import type { ReactiveController, ReactiveControllerHost } from 'lit';
import { getLockStore, type ResourceLockState } from './lock-store';
import { showErrorToast } from '../notifications/easy-toast';
import { personNameById } from '../person-name-by-id';

/**
 * What a page needs to be told by the record's edit lock: another person's lock appearing and
 * ending, and the lock this tab held being lost.
 */
export interface RecordEditLockCallbacks {
  /**
   * The lock this tab held is gone and this tab did not let it go (a takeover, a lapse, a release
   * from elsewhere). The page closes any open edit, since it can no longer be saved.
   */
  onLost?: () => void;
  /**
   * Another person's lock on the record has ended. The page re-reads the record, so the values
   * they saved appear without a reload.
   */
  onOtherReleased?: () => void;
}

/**
 * The edit lock on one record, as a page holds it (#398 C11 for the company, C13a for the site and
 * the person, D-163). Built once so every lockable page behaves alike (the architect: "should all
 * work and look the same"):
 *
 * - it watches the record's lock through the lock store, which reads the status on first watch and
 *   follows the live lock topic after;
 * - `lockedByOther`, `holderUserId` and `expiresAt` describe another person's lock, for the page's
 *   `agwa-lock-held-banner` and for withdrawing the actions that would collide with it;
 * - `loss` is set when the lock this tab held is gone without this tab letting it go, for the
 *   page's `agwa-lock-lost-banner` (`takenOverByUserId` names the new holder of a takeover);
 * - `take()` asks for the lock and, refused, tells the person who holds it;
 * - leaving the page, or watching another record, releases a lock this tab holds, so nobody else
 *   is kept out until the lease lapses.
 *
 * The page decides when to take and release (on Edit, around a confirmation, on opening); this
 * holds no opinion about that.
 */
export class RecordEditLock implements ReactiveController {
  /** Another person holds the record's lock. */
  lockedByOther = false;
  /** Who holds it, named through the directory by id (D-151, D-162). */
  holderUserId: string | null = null;
  /** When their lease lapses, if known. */
  expiresAt: Date | null = null;
  /** Set when the lock THIS tab held is gone, until dismissed or taken again. */
  loss: { takenOverByUserId?: string } | null = null;

  /** Whether this tab held the lock at the last report, so a move to not holding it reads as a loss. */
  private wasOurs = false;
  /** The record being watched; '' when none. */
  private resourceId = '';
  private unsubscribe: (() => void) | null = null;

  constructor(
    private readonly host: ReactiveControllerHost,
    private readonly resourceType: string,
    private readonly callbacks: RecordEditLockCallbacks = {}
  ) {
    host.addController(this);
  }

  /** Whether this tab holds the lock now. */
  get held(): boolean {
    return this.wasOurs;
  }

  /**
   * Watches the lock of the record `resourceId`, letting go of a record watched before. An empty
   * id watches nothing (a record not yet created has nothing to lock).
   */
  watch(resourceId: string) {
    if (this.resourceId === resourceId) return;
    this.unwatch();
    if (!resourceId) return;
    this.resourceId = resourceId;
    this.unsubscribe = getLockStore().subscribe(
      this.resourceType,
      resourceId,
      (state) => this.onState(state)
    );
  }

  /** Stops watching, releasing the lock first if this tab holds it. */
  unwatch() {
    if (this.resourceId && this.wasOurs) {
      void getLockStore().releaseLock(this.resourceType, this.resourceId);
    }
    this.unsubscribe?.();
    this.unsubscribe = null;
    this.resourceId = '';
    this.wasOurs = false;
    this.lockedByOther = false;
    this.holderUserId = null;
    this.expiresAt = null;
    this.loss = null;
    this.host.requestUpdate();
  }

  /**
   * Takes the record's lock for this tab, answering whether it is held. A refusal is told, naming
   * whoever holds it; a request the server never answered is presented by the connection monitor
   * and needs no word here (D-144). `quiet` leaves out the "locked by" toast only: for a page that
   * takes the lock as it opens, where the held banner already names whoever has it and nobody
   * pressed anything. Any other refusal (no editing session, a refusal of the person, a server
   * error) has no banner to say it, so it is told even when quiet; otherwise the page would turn
   * read-only with no word of why.
   */
  async take(options: { quiet?: boolean } = {}): Promise<boolean> {
    if (!this.resourceId) return false;
    const result = await getLockStore().acquireLock(
      this.resourceType,
      this.resourceId
    );
    if (result.success) return true;
    if (result.unanswered) return false;
    if (options.quiet && result.currentHolder) return false;
    // The holder's name is composed here from the directory and shown as it stands; any other
    // refusal is the server's own, passed as sent so the toast resolves it once (@VOC-SERVER-002).
    showErrorToast(
      'Unable to Edit',
      result.currentHolder
        ? `Locked for editing by ${await personNameById(result.currentHolder.userId)}.`
        : (result.errorKept ??
            result.errorMessage ??
            'Could not take the edit lock.')
    );
    return false;
  }

  /** Releases the lock this tab holds; its own release, never reported as a loss. */
  async release(): Promise<void> {
    if (!this.resourceId) return;
    await getLockStore().releaseLock(this.resourceType, this.resourceId);
  }

  /** The person dismissed the lost banner. */
  dismissLoss() {
    this.loss = null;
    this.host.requestUpdate();
  }

  hostConnected() {
    // Nothing to resume: the page calls watch() with the record it shows.
  }

  /** Leaving the page lets go of the lock it holds. */
  hostDisconnected() {
    this.unwatch();
  }

  /**
   * One report of the record's lock. A move from this tab holding it to not, that this tab did not
   * cause (its own release), is a loss. Another person's lock ending tells the page to re-read.
   */
  private onState(state: ResourceLockState) {
    const nowOurs = state.isOwner || state.heldByThisSession;
    const lost = this.wasOurs && !nowOurs && !state.releasedByThisTab;
    if (lost) {
      this.loss = {
        takenOverByUserId: state.takenOver ? state.holder?.userId : undefined,
      };
    } else if (nowOurs) {
      this.loss = null;
    }
    this.wasOurs = nowOurs;

    const otherHolds = !nowOurs && !!state.holder;
    const otherEnded = this.lockedByOther && !otherHolds;
    this.lockedByOther = otherHolds;
    this.holderUserId = otherHolds ? (state.holder?.userId ?? null) : null;
    this.expiresAt = otherHolds ? (state.leaseExpiresAt ?? null) : null;
    this.host.requestUpdate();

    if (lost) this.callbacks.onLost?.();
    if (otherEnded) this.callbacks.onOtherReleased?.();
  }
}
